/* eslint-disable react/no-array-index-key */
import styled from 'styled-components';
import type { ProductListItemModel, SplashModel } from 'autogen/swagger/all';
import { ImageSplash, PictogramSplash, TagSplash } from 'components/shared/Splash';
import { filterSplashesByImage } from 'components/shared/Splash/shared/helpers';

/* #region styles */
const Container = styled.div`
    position: absolute;
    top: ${({ theme }) => theme.Spacing8};
    left: ${({ theme }) => theme.Spacing8};
    width: calc(100% - (${({ theme }) => theme.Spacing8} * 2));
    height: calc(100% - (${({ theme }) => theme.Spacing8} * 2));
    display: flex;
    flex-direction: column;
    z-index: 1;
    ${({ theme }) => theme.SplashOverlay}
`;

const TagSplashContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.Spacing4};
`;

const ExtraSplashesContainer = styled.div`
    flex: 0 1 100%;
    display: grid;
    align-content: end;
    gap: ${({ theme }) => theme.Spacing8};
`;

const PictogramContainer = styled.div`
    display: grid;
    align-content: end;
    gap: ${({ theme }) => theme.Spacing2};
`;

const StyledTagSplash = styled(TagSplash)`
    margin-right: auto;
`;

/* #endregion */

function TagContainer({
    primary,
    secondary
}: {
    primary: SplashModel['primary'];
    secondary: SplashModel['secondary'];
}): JSX.Element {
    return (
        <TagSplashContainer>
            {primary && <TagSplash {...primary} />}
            {secondary && <TagSplash {...secondary} />}
        </TagSplashContainer>
    );
}

function ExtraContainer({ splashes }: { splashes: SplashModel['extra'] }): JSX.Element {
    const pictogramSplashes = splashes.filter((splash) => splash.type === 'Hazard');
    const imageSplashes = filterSplashesByImage(splashes);
    const minimumAgeSplash = splashes.filter((splash) => splash.type === 'OtcMinAge');

    return (
        <ExtraSplashesContainer>
            {pictogramSplashes && (
                <PictogramContainer>
                    {pictogramSplashes.map((splash, index) => (
                        <PictogramSplash key={index} {...splash} />
                    ))}
                </PictogramContainer>
            )}
            {imageSplashes?.map((splash, index) => (
                <ImageSplash key={index} {...splash} />
            ))}
            {minimumAgeSplash?.map((splash, index) => (
                <StyledTagSplash key={index} {...splash} />
            ))}
        </ExtraSplashesContainer>
    );
}

/* Vertical Product item splashOverlay */
export default function SplashOverlay({ splashes, ...restProps }: { splashes: ProductListItemModel['splashes'] }) {
    const { extra, primary, secondary } = splashes;

    return (
        <Container {...restProps}>
            {(primary || secondary) && <TagContainer primary={primary} secondary={secondary} />}
            {extra.length > 0 && <ExtraContainer splashes={extra} />}
        </Container>
    );
}
